import { useRef, useState } from "react";
import {
  TEMPLATE_MEDIA_LIMITS,
  type TemplateMediaKind,
  type WabaTemplateMedia,
} from "@school-sales-ai/shared";

/**
 * Picks the file that goes in a template's header.
 *
 * Used in TWO places on purpose — the template editor (where the file is the
 * sample Meta reviews) and the broadcast dialog (where it is what actually goes
 * out). One component so the validation copy, the preview and the size rules
 * cannot drift between the screen that authors a brochure and the screen that
 * mails it to 500 people.
 *
 * Shaped after `LogoSettings`: a hidden file input, the `e.target.value = ""`
 * reset so re-picking the same file still fires, and a client-side check that is
 * convenience rather than a gate — the backend re-checks the type, sniffs the
 * magic bytes and re-checks the size. Its real job is sparing the admin a raw
 * `413 FST_REQ_FILE_TOO_LARGE`, which the multipart layer raises before any
 * Spanish message of ours is reachable.
 */
export function HeaderMediaPicker({
  kind,
  media,
  onPick,
  isUploading,
  error,
}: {
  kind: TemplateMediaKind;
  media: WabaTemplateMedia | null;
  onPick: (file: File) => void;
  isUploading: boolean;
  /** A server-side failure, rendered beside our own client-side one. */
  error?: string;
}): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const limits = TEMPLATE_MEDIA_LIMITS[kind];

  function handleFile(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!(limits.mimeTypes as readonly string[]).includes(file.type)) {
      setLocalError(
        kind === "image"
          ? "Formato no permitido. Usa JPG o PNG."
          : "Formato no permitido. Usa PDF.",
      );
      return;
    }
    if (file.size > limits.maxSizeMb * 1024 * 1024) {
      setLocalError(`El archivo supera el límite de ${limits.maxSizeMb} MB.`);
      return;
    }
    setLocalError(null);
    onPick(file);
  }

  const shown = localError ?? error;

  return (
    <div className="mt-2">
      <div className="flex items-start gap-3">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-ink-200 bg-ink-050">
          {media === null ? (
            <span className="text-xs text-ink-500">{kind === "image" ? "Imagen" : "PDF"}</span>
          ) : kind === "image" && media.previewUrl ? (
            <img
              src={media.previewUrl}
              alt="Encabezado de la plantilla"
              className="h-full w-full object-contain"
            />
          ) : (
            // A PDF has no thumbnail, and an image whose signed URL could not be
            // minted must still show that a file IS attached — otherwise the
            // admin re-uploads one that is already there.
            <span className="px-1 text-center text-[10px] leading-tight text-ink-600">
              {kind === "image" ? "Imagen cargada" : "PDF"}
            </span>
          )}
        </div>

        <div className="min-w-0">
          {media !== null ? (
            <p className="truncate text-sm text-ink-800" title={media.fileName}>
              {media.fileName}
            </p>
          ) : null}
          <input
            ref={inputRef}
            type="file"
            accept={limits.accept}
            onChange={handleFile}
            className="hidden"
            aria-label={kind === "image" ? "Seleccionar imagen" : "Seleccionar documento"}
          />
          <button
            type="button"
            disabled={isUploading}
            onClick={() => inputRef.current?.click()}
            className="mt-1 rounded-md border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-900 disabled:opacity-40"
          >
            {isUploading
              ? "Subiendo…"
              : media === null
                ? kind === "image"
                  ? "Subir imagen"
                  : "Subir PDF"
                : "Cambiar archivo"}
          </button>
          <p className="mt-1 text-xs text-ink-500">
            {kind === "image" ? "JPG o PNG" : "PDF"}, máximo {limits.maxSizeMb} MB.
          </p>
        </div>
      </div>

      {shown ? <p className="mt-1 text-xs text-red-600">{shown}</p> : null}
    </div>
  );
}
